import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Features } from '@nimbus-assistant/enums/features';
import { Language } from '@nimbus-assistant/enums/language';
import { WindowPosition } from '@nimbus-assistant/enums/window-position';
import { LoggingService } from '@nimbus-assistant/services/logging.service';
import { MessageService } from '@nimbus-assistant/services/message.service';
import { RoutingService } from '@nimbus-assistant/services/routing.service';
import { SettingsService } from '@nimbus-assistant/services/settings.service';
import { UserSettingsService } from '@nimbus-assistant/services/user-settings.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent implements OnInit {
  private readonly settingsWidth = 360;
  private readonly settingsHeight = 600;

  private get zoomControl(): AbstractControl {
    return (this.form?.get('appearance') as UntypedFormGroup)?.controls['zoom'];
  }

  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;

  public readonly settingsTabGeneral = 'general';
  public readonly settingsTabNotifications = 'notifications';
  public readonly settingsTabAppearance = 'appearance';
  public tabItems = [];

  public form!: UntypedFormGroup;
  public status: string;

  public constructor(
    private formBuilder: UntypedFormBuilder,
    private readonly settings: UserSettingsService,
    private readonly settingsService: SettingsService,
    private readonly loggingService: LoggingService,
    private readonly routingService: RoutingService,
    private readonly messageService: MessageService,
  ) {
    this.navigateTo(this.settingsTabGeneral);
    const audioNotificationsEnabled = this.settingsService.supportsFeature(Features.AudioNotifications);
    this.tabItems = [
      { id: this.settingsTabGeneral, name: TranslationKeys.UserPreferencesTabGeneral, isActive: true, isVisible: true },
      {
        id: this.settingsTabNotifications,
        name: TranslationKeys.UserPreferencesTabNotifications,
        isActive: false,
        isVisible: audioNotificationsEnabled,
      },
      { id: this.settingsTabAppearance, name: TranslationKeys.UserPreferencesTabAppearance, isActive: false, isVisible: true },
    ];
  }

  public ngOnInit(): void {
    MessageService.SendResizeWindow(this.settingsWidth, this.settingsHeight);
    MessageService.SendCenterCurrentWindow();

    const preCallInfoEnabled = this.settingsService.supportsFeature(Features.PreCallInfo);

    const details = this.settings.userSettings;
    this.form = this.formBuilder.group({
      general: this.formBuilder.group({
        language: [details.language || Language.en, Validators.required],
        position: [details.position.toString() || WindowPosition.BottomRight.toString(), Validators.required],
        autorun: details.autorun,
        zoom: details.zoom || 1,
        openLoginWindowOnStartup: details.openLoginWindowOnStartup,
        onTopTeamToast: details.onTopTeamToast,
        preCallInfo: [
          { value: details.preCallInfo, disabled: !preCallInfoEnabled || !details.onTopTeamToast },
          details.onTopTeamToast && preCallInfoEnabled ? [Validators.required] : [],
        ],
      }),
      notifications: this.formBuilder.group({
        useAudioForExternalTasks: details.useAudioForExternalTasks,
        audioForExternalTasks: [{ value: details.audioForExternalTasks, disabled: !details.useAudioForExternalTasks }, Validators.required],
        useAudioForInstantMessages: details.useAudioForInstantMessages,
        audioForInstantMessages: [
          { value: details.audioForInstantMessages, disabled: !details.useAudioForInstantMessages },
          Validators.required,
        ],
        useAudioForEmails: details.useAudioForEmails,
        audioForEmails: [{ value: details.audioForEmails, disabled: !details.useAudioForEmails }, Validators.required],
      }),
      appearance: this.formBuilder.group({
        position: [details.position.toString() || WindowPosition.BottomRight.toString(), Validators.required],
        zoom: details.zoom || 1,
        bringToForeground: details.bringToForeground,
        topmost: details.topmost,
      }),
    });

    this.resetByFlag('notifications.useAudioForExternalTasks', 'notifications.audioForExternalTasks');
    this.resetByFlag('notifications.useAudioForInstantMessages', 'notifications.audioForInstantMessages');
    this.resetByFlag('notifications.useAudioForEmails', 'notifications.audioForEmails');

    if (preCallInfoEnabled) {
      this.resetByFlag('general.onTopTeamToast', 'general.preCallInfo');
    }

    this.zoomControl.valueChanges.pipe(untilDestroyed(this)).subscribe((zoom) => {
      MessageService.SendUpdateSettings({
        ...this.settings.userSettings,
        settingsWidth: this.settingsWidth,
        settingsHeight: this.settingsHeight,
      });
      MessageService.SendPreviewZoom(zoom);
    });
  }

  public cancel(): void {
    this.messageService.sendRepositionCurrentWindow();
    this.routingService.goToDefaultPage();
  }

  public save(): void {
    this.settings.userSettings.language = this.form.value.general.language;
    this.settings.userSettings.autorun = this.form.value.general.autorun;
    this.settings.userSettings.openLoginWindowOnStartup = this.form.value.general.openLoginWindowOnStartup;
    this.settings.userSettings.onTopTeamToast = this.form.value.general.onTopTeamToast;
    this.settings.userSettings.preCallInfo = this.form.value.general.preCallInfo;

    this.settings.userSettings.useAudioForExternalTasks = this.form.value.notifications.useAudioForExternalTasks;
    this.settings.userSettings.audioForExternalTasks = this.form.value.notifications.audioForExternalTasks;
    this.settings.userSettings.useAudioForInstantMessages = this.form.value.notifications.useAudioForInstantMessages;
    this.settings.userSettings.audioForInstantMessages = this.form.value.notifications.audioForInstantMessages;
    this.settings.userSettings.useAudioForEmails = this.form.value.notifications.useAudioForEmails;
    this.settings.userSettings.audioForEmails = this.form.value.notifications.audioForEmails;

    this.settings.userSettings.position = this.form.value.appearance.position;
    this.settings.userSettings.zoom = this.form.value.appearance.zoom;
    this.settings.userSettings.bringToForeground = this.form.value.appearance.bringToForeground;
    this.settings.userSettings.topmost = this.form.value.appearance.topmost;

    MessageService.SendUpdateSettings(this.settings.userSettings);
    try {
      MessageService.translateTrayIcon();
    } catch (e) {
      this.loggingService.Error(`Could not translate TrayIconMessage: ${JSON.stringify(e)}`);
    }
    this.cancel();
  }

  public navigateTo(id: string): void {
    this.tabItems.forEach((tab) => (tab.isActive = tab.id === id));
  }

  public isTabActive(id: string): boolean {
    return this.tabItems.find((tab) => tab.id === id)?.isActive;
  }

  private resetByFlag(flag: string, dependantField: string): void {
    this.form
      .get(flag)
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((enabled) => {
        if (enabled) {
          this.form.get(dependantField)?.enable();
          this.form.get(dependantField)?.markAllAsTouched();
        } else {
          this.form.get(dependantField)?.setValue(null);
          this.form.get(dependantField)?.disable();
        }
      });
  }
}
